<template>
  <div>
    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <el-form
      @submit.prevent="openCashierPinDialog"
      novalidate
      :model="expense"
      :rules="rules"
      label-position="top"
      ref="expenseForm"
      class="expense-page"
    >
      <div class="expense-layout">
        <div class="expense-actions">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            :disabled="saving"
            @click.prevent="openCashierPinDialog"
          />
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('expenseForm')"
          />
        </div>

        <h1>{{ t('sections.newExpense') }}</h1>

        <div class="expense-form-row">
          <div class="expense-fields">
            <!-- expense amount -->
            <el-form-item :label="t('amount')" prop="amount" class="input-label amount-form-item">
              <money-input
                id="amount"
                class="amount-input-green"
                :currency="$ctx.currency"
                :show-currency-prefix="true"
                v-model="expense.amount"
                ref="amountRef"
                autofocus
                @input="onAmountInput"
              />
            </el-form-item>

            <!-- payee -->
            <el-form-item :label="t('payee')" class="input-label" prop="payToAccountID">
              <el-select
                id="debited-acount"
                v-model="expense.payToAccountID"
                :trigger-on-focus="false"
                value-key="name"
                class="w-full"
                filterable
                remote
                :remote-method="searchExpensesAccounts"
              >
                <el-option
                  v-for="option in expenseAccounts"
                  :key="option.id"
                  :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                  :value="option.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item :label="t('paidFrom')" prop="payFromAccountID" class="input-label">
              <el-select
                id="credit-account"
                v-model="expense.payFromAccountID"
                :trigger-on-focus="false"
                value-key="name"
                class="w-full"
                filterable
                remote
                :remote-method="searchAccounts"
              >
                <el-option
                  :key="registerAccount.id"
                  :label="$ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName"
                  :value="registerAccount.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="safe in safes"
                  :key="safe.id"
                  :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
                  :value="safe.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="bank in banks"
                  :key="bank.id"
                  :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
                  :value="bank.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="option in accounts"
                  :key="option.id"
                  :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                  :value="option.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <!-- what for -->
            <el-form-item :label="t('whatsFor')" class="input-label whats-for-form-item">
              <el-input
                id="whats-for"
                v-model="expense.whatsFor"
                :placeholder="t('whatsForPlaceholder')"
                maxlength="500"
                clearable
              />
            </el-form-item>

            <!-- notes -->
            <el-form-item :label="t('notes')">
              <el-input v-model="expense.notes" :rows="4" type="textarea" />
            </el-form-item>
          </div>

          <div class="expense-digit-pad">
            <digit-pad @press="onDigitPadPress" />
          </div>
        </div>
      </div>
    </el-form>
    <el-dialog v-model="cashierPinDialogOpen" width="30%">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('dialogs.enterCashierPin') }}</p>
        </div>

        <!-- password input up to 6 chars -->
        <div class="my-2">
          <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            ref="cashierPinInput"
            @keyup.enter="validateSubmit('expenseForm')"
          />
        </div>

        <!-- finish payment -->
        <div class="flex items-center justify-between mb-2">
          <horizontal-button
            class="text-lg complete-payment"
            :title="t('dialogs.completePay')"
            @click="validateSubmit('expenseForm')"
            :disabled="saving"
            :rounded="false"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
// models

import { Expense } from './Expenses.vue'
import { expenseI18nMessages } from './Expenses.vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import DigitPad, { type DigitPadValue } from '@/components/form/DigitPad.vue'
import { ElForm } from 'element-plus'
import MoneyInput from '@/components/form/MoneyInput.vue'
import { useI18n } from 'vue-i18n'
import { Account } from '@/models/accounting/Account'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'new-expense-form',
  setup() {
    const messages = {
      en: {
        ...expenseI18nMessages.en,
        sections: {
          newExpense: 'New Expense'
        },
        success: 'Expense has been recoded'
      },
      ar: {
        ...expenseI18nMessages.ar,
        sections: {
          newExpense: 'تسجيل مصروف جديد'
        },
        success: 'تم تسجيل المصروف'
      }
    }
    const { t } = useI18n({ messages, useScope: 'global' })
    const rules = {
      // expenseDate: [{ required: true, message: t('validation.required') }],
      amount: [{ required: true, message: t('validation.required') }],
      payFromAccountID: [{ required: true, message: t('validation.required') }],
      payToAccountID: [{ required: true, message: t('validation.required') }]
    }
    return { t, rules }
  },
  components: {
    HorizontalButton,
    AlertBadge,
    MoneyInput,
    DigitPad
  },

  data() {
    const accounts: Account[] = []
    const expenseAccounts: Account[] = []
    const registerAccount: Account = new Account()
    const safes: Account[] = []
    const banks: Account[] = []

    const cashierPinDialogOpen = ref(false)
    const cashierPin = ref('')

    return {
      expense: new Expense(),
      amountRaw: '',
      error: { title: '', body: '' },
      accounts,
      registerAccount,
      safes,
      banks,
      expenseAccounts,
      cashierPinDialogOpen,
      cashierPin,
      saving: false
    }
  },
  mounted() {
    this.$nextTick(() => {
      const amountRef = this.$refs.amountRef as InstanceType<typeof MoneyInput> | undefined
      amountRef?.focus?.()
    })
  },
  async beforeMount() {
    try {
      this.registerAccount = Account.from(
        await this.$http.get<Account>(`${Account.ENDPOINT}/${this.$ctx.currentRegister.accountID}`)
      )
      const safesResp = await this.$http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
      this.safes = safesResp.map((s) => Account.from(s))
      const banksResp = await this.$http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
      this.banks = banksResp.map((b) => Account.from(b))
    } catch (error: any) {
      this.error.title = error?.title
      this.error.body = error?.body
    }
  },
  methods: {
    async searchAccounts(query: string) {
      if (query) {
        try {
          const url = `${Account.ENDPOINT}?q=${query}`
          this.accounts = await this.$http.get<Account[]>(url)
        } catch (error: any) {
          this.$alertModal.showDanger({ title: error?.title })
          this.accounts = []
        }
      }
    },

    async searchExpensesAccounts(query: string) {
      if (query) {
        try {
          const url = `${Account.ENDPOINT}?account-category=expenses&q=${query}`
          this.expenseAccounts = await this.$http.get<Account[]>(url)
        } catch (error: any) {
          this.$alertModal.showDanger({ title: error?.title })
          this.expenseAccounts = []
        }
      }
    },

    applyAmountFromRaw() {
      const amount = this.amountRaw === '' ? 0 : Number(this.amountRaw)
      this.expense.amount = Number.isFinite(amount) ? amount : 0
      const amountRef = this.$refs.amountRef as InstanceType<typeof MoneyInput> | undefined
      if (amountRef?.setValue) {
        amountRef.setValue(this.expense.amount || null)
      } else if (amountRef?.updateValue) {
        amountRef.updateValue(this.expense.amount)
      }
    },

    onAmountInput(val: number | null) {
      if (val === null || val === undefined || Number.isNaN(val)) {
        this.amountRaw = ''
        return
      }
      this.amountRaw = String(Math.trunc(Math.abs(val)))
    },

    onDigitPadPress(key: DigitPadValue) {
      if (key === 'delete') {
        this.amountRaw = ''
        this.applyAmountFromRaw()
        return
      }

      if (key === 'backspace') {
        this.amountRaw = this.amountRaw.slice(0, -1)
        this.applyAmountFromRaw()
        return
      }

      // Avoid a leading pile of zeros unless the pad explicitly adds 00/000 after a value
      if (this.amountRaw === '0' && key !== '00' && key !== '000') {
        this.amountRaw = key
      } else if (this.amountRaw === '' && (key === '00' || key === '000')) {
        this.amountRaw = '0'
      } else {
        const next = `${this.amountRaw}${key}`
        // Match MoneyInput maxlength safety (~15 digits)
        if (next.length > 15) return
        this.amountRaw = next
      }

      this.applyAmountFromRaw()
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      this.expense = new Expense()
      this.amountRaw = ''
      this.$globalEmitter.emit('money-input-reset')
      form.resetFields()
    },

    openCashierPinDialog() {
      if (this.saving) return
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        this.cashierPinDialogOpen = true
        this.$nextTick(() => {
          setTimeout(() => {
            /* @ts-ignore */
            this.$refs['cashierPinInput'].focus()
          }, 0)
        })
        return
      }
      this.validateSubmit('expenseForm')
    },

    async validateSubmit(formName: string) {
      this.error.title = ''

      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.cashierPinDialogOpen = false
          this.error.title = this.t('dialogs.errCashierPinRequired')
          return
        }
      }

      this.expense.currency = this.$ctx.currency
      this.expense.precision = this.$ctx.currencyPrecision

      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          this.cashierPinDialogOpen = false
          return false
        }
        // Block double-submits while the request is in flight (#1141)
        if (this.saving) return false
        this.saving = true
        try {
          this.cashierPinDialogOpen = false
          const result = await this.$http.post<Expense>('/accounting/company', this.expense, {
            headers: { pin: this.cashierPin }
          })
          this.$alertModal.showSuccess({ title: this.t('success') })
          this.$router.push(
            this.$Route.ACCOUNTING_TRANSACTIONS_TRANSACTION.replace(':id', result.transactionID || '')
          )
        } catch (error: any) {
          this.error.title = error?.title
          this.error.body = error?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } finally {
          this.saving = false
        }

        return true
      })
    }
  }
})
</script>

<style scoped>
h1 {
  @apply text-lg font-bold;
  margin: 0 0 4px;
  padding: 0;
  line-height: 1.3;
}
h2 {
  @apply py-3 font-bold;
}
h3 {
  @apply py-3 font-bold;
}
.remove-button {
  @apply text-lg;
  color: var(--danger-text-color);
  align-self: center;
  justify-self: center;
  cursor: pointer;
  margin: 0.5rem;
}
.remove-button:hover {
  color: var(--danger-hover-bg-color);
  align-self: center;
  justify-self: center;
}
.fully-paid-button {
  height: 40px;
}
hr.solid {
  border-top: 1px solid var(--tertiary-bg-color);
}
.hide {
  display: none;
}
.input:hover + .hide {
  display: flex;
  justify-content: center;
}
.danger {
  color: var(--danger-text-color);
  background: var(--danger-bg-color);
}
.success {
  color: var(--success-text-color);
  background: var(--success-bg-color);
}
td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
  text-align: center;
}
tr:nth-child(even) {
  background-color: #f3f3f3;
}

.expense-page {
  display: flex;
  justify-content: center;
  width: 100%;
}

.expense-layout {
  width: max-content;
  max-width: 100%;
  padding: 0 1.5rem 2rem;
}

.expense-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin: 0 0 24px;
}

.expense-actions :deep(.button) {
  width: auto;
  min-width: 9.5rem;
  padding: 0.5rem 1.1rem;
}

.expense-actions :deep(.button > span) {
  width: auto;
  max-width: 100%;
  justify-content: center;
  gap: 0.55rem;
}

.expense-actions :deep(.button > span > span) {
  width: auto;
}

.expense-form-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
}

.expense-fields {
  width: 100%;
  min-width: 0;
}

.expense-fields :deep(.el-form-item) {
  margin-bottom: 20px;
}

.expense-fields :deep(.el-form-item:last-child) {
  margin-bottom: 0;
}

.expense-fields :deep(.el-form-item__label) {
  height: auto !important;
  line-height: 1.25 !important;
  padding-bottom: 0 !important;
  margin-bottom: 6px !important;
}

.expense-fields :deep(.whats-for-form-item) {
  margin-bottom: 20px;
}

.expense-digit-pad {
  display: flex;
  justify-content: center;
  width: 100%;
}

@screen md {
  .expense-form-row {
    flex-direction: row;
    align-items: flex-start;
    gap: 36px;
  }

  .expense-fields {
    flex: none;
    width: 26rem;
    max-width: 26rem;
  }

  .expense-digit-pad {
    flex: none;
    width: 16rem;
    justify-content: flex-start;
    /* Skip the Amount label + 6px gap so the pad lines up with the input */
    padding-top: 39px;
  }
}

.amount-form-item :deep(.el-input.el-input__wrapper),
.amount-form-item :deep(.amount-input-green) {
  min-height: 4.25rem;
  box-shadow: 0 0 0 2px #42deac inset !important;
  border-radius: 0.35rem;
}

.amount-form-item :deep(.el-input__inner) {
  min-height: 4rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.amount-form-item :deep(.el-input__prefix-inner) {
  font-size: 1.5rem;
  font-weight: 600;
}

.amount-form-item :deep(.el-input.el-input__wrapper.is-focus),
.amount-form-item :deep(.amount-input-green.is-focus) {
  box-shadow: 0 0 0 2px #42deac inset !important;
}
</style>
